import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import { bytesToMegabytes, isFileFormatValid, isFileSizeValid } from '../../helpers/fileUtils';

import { Badge } from '../index';
import FileIcon from '../Icons/FileIcon';
import ErrorAlert from '../ErrorAlert/ErrorAlert';

const MultiFileInput = ({
    files: _files = [],
    handleFiles,
    maxSizeInBytes,
    minSizeInBytes,
    formatsArray = null,
    state = 'default',
    placeholder = 'Drag & drop a file to upload or click to browse',
}) => {
    const files = _files || [];

    const singleInputRef = useRef(null);
    const multipleInputRef = useRef(null);

    const replaceFileIndex = useRef(null);

    const [errorAlert, setErrorAlert] = useState(null);

    const isDisabled = state === 'disabled';
    const isError = state === 'error';

    const [{ isOver }, drop] = useDrop({
        accept: [NativeTypes.FILE],
        drop: handleDrop,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    function handleDrop(item, monitor) {
        if (isDisabled) {
            return;
        }

        const droppedFiles = monitor.getItem().files;

        if (droppedFiles && !!droppedFiles?.length) {
            const validFiles = [];
            const errorFiles = [];

            const shouldCheckFormats = !!formatsArray && !!formatsArray?.length;

            for (const file of droppedFiles) {
                const isDuplicate = checkIfDuplicate(file);

                if (isDuplicate) {
                    errorFiles.push({ error: ['duplicate'], file });
                    continue;
                }

                const isSizeValid = isFileSizeValid({ file, maxSizeInBytes, minSizeInBytes });
                const isFormatValid = shouldCheckFormats
                    ? isFileFormatValid({ file, availableFormats: formatsArray })
                    : true;

                if (isSizeValid && isFormatValid) {
                    validFiles.push(file);
                } else {
                    const error = [];
                    if (!isFormatValid) {
                        error.push('format');
                    }
                    if (!isSizeValid) {
                        error.push('size');
                    }
                    errorFiles.push({ error, file });
                }
            }

            if (!!validFiles.length) {
                handleFiles([...(files || []), ...validFiles]);
            }

            if (!!errorFiles.length) {
                const message = generateErrorMessage(errorFiles);
                setErrorAlert({ message });
            }
        }
    }

    const handleChange = (e) => {
        e?.preventDefault();

        const newFiles = Array.from(e.target.files);

        const validFiles = [];
        const errorFiles = [];

        for (const file of newFiles) {
            const isDuplicate = checkIfDuplicate(file);

            if (isDuplicate) {
                errorFiles.push({ error: ['duplicate'], file });
                continue;
            }

            const isSizeValid = isFileSizeValid({ file, maxSizeInBytes, minSizeInBytes });
            if (isSizeValid) {
                validFiles.push(file);
            } else {
                errorFiles.push({ error: ['size'], file });
            }
        }

        if (!!validFiles.length) {
            handleFiles([...(files || []), ...validFiles]);
        }

        if (!!errorFiles.length) {
            const message = generateErrorMessage(errorFiles);
            setErrorAlert({ message });
        }
    };

    const handleReplace = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const index = replaceFileIndex.current;
        if (typeof index === 'number') {
            const isDuplicate = checkIfDuplicate(file);

            if (isDuplicate) {
                const message = 'This file is already uploaded.';
                setErrorAlert({ message });
                return;
            }

            const isSizeValid = isFileSizeValid({ file, maxSizeInBytes, minSizeInBytes });
            if (isSizeValid) {
                const updatedFiles = files.map((f, i) => (i === index ? file : f));
                handleFiles(updatedFiles);
            } else {
                const message = '';
                setErrorAlert({ message });
            }
        }
    };

    function checkIfDuplicate(file) {
        return files?.some((f) => {
            const fName = typeof f === 'string' ? f : f.name;
            const fSize = typeof f === 'string' ? null : f.size;
            return fName === file.name && fSize === file.size;
        });
    }

    function generateErrorMessage(errorFiles) {
        if (!errorFiles.length) return '';

        const formatsString = formatsArray
            ?.map((format) => format.slice(1)?.toUpperCase())
            ?.join(', ');
        const duplicateMessage = 'You tried to upload a file that is already added.';

        const seenErrors = new Set();

        for (const { error } of errorFiles) {
            for (const type of error) {
                seenErrors.add(type);
            }
        }

        const messages = [];

        if (seenErrors.has('duplicate')) {
            messages.push(duplicateMessage);
        }

        if (seenErrors.has('format')) {
            messages.push(`Allowed file types: ${formatsString}`);
        }

        if (seenErrors.has('size')) {
            const minLabel = minSizeInBytes ? `${bytesToMegabytes(minSizeInBytes)}MB` : null;
            const maxLabel = maxSizeInBytes ? `${bytesToMegabytes(maxSizeInBytes)}MB` : null;

            if (minLabel && maxLabel) {
                messages.push(`File size must be between ${minLabel} and ${maxLabel}.`);
            } else if (minLabel) {
                messages.push(`File size must be at least ${minLabel}.`);
            } else if (maxLabel) {
                messages.push(`File size must not exceed ${maxLabel}.`);
            }
        }

        const count = errorFiles.length;

        return `Couldn't upload ${count > 1 ? `${count} "files"` : 'a file'}. ${messages.join(
            ' '
        )}`;
    }

    const labelClassName = classNames(
        'w-full min-h-[56px] px-4 py-3 flex items-center justify-start rounded-2 border border-dashed bg-purple-100 transition-colors',
        {
            'bg-opacity-60': isOver && !isDisabled,
            'bg-opacity-30': !isOver || isDisabled,
            'border-purple-300': state === 'default',
            'border-neutral-300 cursor-not-allowed': isDisabled,
            'border-red-500': isError,
            'cursor-pointer': !isDisabled,
        }
    );

    return (
        <>
            <div
                ref={drop}
                className={labelClassName}
                onClick={() => multipleInputRef.current.click()}
            >
                <div className="flex items-center gap-x-2 gap-y-1 flex-wrap max-w-full">
                    {files?.map((file, index) => {
                        const path = typeof file === 'string' ? file : file.name;

                        const handleRemove = () => {
                            const filteredFiles = files.filter((_, i) => i !== index);
                            handleFiles(filteredFiles);
                        };

                        const onBadgeClick = (e) => {
                            e?.stopPropagation();
                            e?.preventDefault();
                            replaceFileIndex.current = index;
                            singleInputRef.current.click();
                        };

                        return (
                            <div key={index} className="max-w-full">
                                <Badge
                                    text={path}
                                    color="purple"
                                    leadingIcon={FileIcon}
                                    removeable={!isDisabled}
                                    badgeContentColor="#754DCF"
                                    customTextStyles="-ml-1"
                                    handleRemove={handleRemove}
                                    onBadgeClick={onBadgeClick}
                                />
                            </div>
                        );
                    })}
                </div>

                {!files?.length && (
                    <p className="font-body text-body-regular-m text-neutral-300/70">
                        {placeholder}
                    </p>
                )}
            </div>

            <input
                type="file"
                className="hidden"
                accept={formatsArray?.join(', ')}
                onChange={handleReplace}
                disabled={isDisabled}
                ref={singleInputRef}
            />

            <input
                type="file"
                className="hidden"
                accept={formatsArray?.join(', ')}
                multiple
                onChange={handleChange}
                disabled={isDisabled}
                ref={multipleInputRef}
            />

            <ErrorAlert
                errorAlert={errorAlert}
                setErrorAlert={setErrorAlert}
                autoCloseInMS={5000}
            />
        </>
    );
};

MultiFileInput.propTypes = {
    files: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(File), PropTypes.string])),
    handleFiles: PropTypes.func.isRequired,
    state: PropTypes.oneOf(['default', 'disabled', 'error']),
    maxSizeInBytes: PropTypes.number,
    minSizeInBytes: PropTypes.number,
    formatsArray: PropTypes.arrayOf(PropTypes.string),
    placeholder: PropTypes.string,
};

export default MultiFileInput;
